import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const STOP_LOCATION_FIELDS = [
  { value: 'stop_location_name', label: 'Stop Location Name' },
  { value: 'stop_location_additional_name', label: 'Stop Location Additional Name' },
  { value: 'stop_location_address', label: 'Stop Location Address' },
  { value: 'stop_location_contact', label: 'Stop Location Contact Name' },
  { value: 'stop_location_phone', label: 'Stop Location Phone' },
  { value: 'stop_location_records_requested', label: 'Stop Location Records Requested' },
];

const GENERAL_FIELDS = [
  { value: 'patient_name', label: 'Patient Name' },
  { value: 'wo_number', label: 'Work Order Number' },
  { value: 'cause_number', label: 'Cause Number' },
  { value: 'ordered_by', label: 'Ordered By' },
  { value: 'insured', label: 'Insured' },
  { value: 'claim_number', label: 'Claim Number' },
  { value: 'adjuster_name', label: 'Adjuster Name' },
  { value: 'file_number', label: 'File Number' },
];

const ATTORNEY_FIELDS = [
  { value: 'client_firm', label: 'Client By Firm' },
  { value: 'client_attorney', label: 'Client By Attorney Name' },
  { value: 'opposing_firm', label: 'Opposing By Firm' },
  { value: 'opposing_attorney', label: 'Opposing By Attorney Name' },
  { value: 'additional_firm', label: 'Additional Attorney By Firm' },
  { value: 'additional_attorney', label: 'Additional By Attorney Name' },
];

const STATUS_COLORS = {
  'Pending': 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Completed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-orange-100 text-orange-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

export default function Search() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('patient_name');
  const [recordType, setRecordType] = useState('pending');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');

  const doSearch = async (text, field, rType) => {
    setLoading(true);
    try {
      const params = { type: rType };

      // Map attorney fields to the API params
      if (field === 'client_firm') {
        params.attorney_firm = text;
        params.attorney_type = 'Client';
      } else if (field === 'client_attorney') {
        params.attorney_name = text;
        params.attorney_type = 'Client';
      } else if (field === 'opposing_firm') {
        params.attorney_firm = text;
        params.attorney_type = 'Opposing';
      } else if (field === 'opposing_attorney') {
        params.attorney_name = text;
        params.attorney_type = 'Opposing';
      } else if (field === 'additional_firm') {
        params.attorney_firm = text;
        params.attorney_type = 'Additional';
      } else if (field === 'additional_attorney') {
        params.attorney_name = text;
        params.attorney_type = 'Additional';
      } else if (text) {
        params[field] = text;
      }

      const res = await axios.get(`${API_URL}/api/causes`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // Flatten to WO-level results
      const rows = [];
      for (const cause of res.data) {
        const wos = cause.work_orders || [];
        if (wos.length === 0) {
          rows.push({
            cause_id: cause.id,
            wo_number: '-',
            legal_tech: '-',
            status: '-',
            type_of_record: '-',
            ordered_by: cause.ordered_by,
            stop_location: '-',
            attorney_firm: cause.attorney_firm || '',
            patient_name: cause.patient_name,
            rush_order: cause.rush_order,
          });
        }
        for (const wo of wos) {
          rows.push({
            cause_id: cause.id,
            wo_id: wo.id,
            wo_number: wo.wo_number,
            legal_tech: wo.legal_tech,
            status: wo.status,
            type_of_record: wo.type_of_record,
            ordered_by: cause.ordered_by,
            stop_location: wo.stop_location_name,
            attorney_firm: cause.attorney_firm || '',
            patient_name: cause.patient_name,
            rush_order: wo.rush_order || cause.rush_order,
          });
        }
      }
      setResults(rows);
    } catch (err) {
      console.error('Search failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    doSearch('', 'patient_name', 'pending');
    // eslint-disable-next-line
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    doSearch(searchText, searchField, recordType);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Search Orders</h1>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Enter search text..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-6 py-2 text-white rounded-md font-medium"
            style={{ backgroundColor: '#1a2744' }}
          >
            Search
          </button>
        </div>
      </form>

      {/* Search criteria */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Column 1: Stop Locations */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2 text-sm">Stop Locations</h3>
            {STOP_LOCATION_FIELDS.map((f) => (
              <label key={f.value} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input
                  type="radio"
                  name="searchField"
                  value={f.value}
                  checked={searchField === f.value}
                  onChange={() => setSearchField(f.value)}
                  className="text-blue-600"
                />
                {f.label}
              </label>
            ))}
          </div>

          {/* Column 2: General */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2 text-sm">General</h3>
            {GENERAL_FIELDS.map((f) => (
              <label key={f.value} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input
                  type="radio"
                  name="searchField"
                  value={f.value}
                  checked={searchField === f.value}
                  onChange={() => setSearchField(f.value)}
                  className="text-blue-600"
                />
                {f.label}
              </label>
            ))}
          </div>

          {/* Column 3: Attorney */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2 text-sm">Attorney</h3>
            {ATTORNEY_FIELDS.map((f) => (
              <label key={f.value} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input
                  type="radio"
                  name="searchField"
                  value={f.value}
                  checked={searchField === f.value}
                  onChange={() => setSearchField(f.value)}
                  className="text-blue-600"
                />
                {f.label}
              </label>
            ))}
          </div>

          {/* Column 4: Type Records */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2 text-sm">Type Records</h3>
            <label className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
              <input
                type="radio"
                name="recordType"
                value="pending"
                checked={recordType === 'pending'}
                onChange={() => setRecordType('pending')}
                className="text-blue-600"
              />
              Pending
            </label>
            <label className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
              <input
                type="radio"
                name="recordType"
                value="all"
                checked={recordType === 'all'}
                onChange={() => setRecordType('all')}
                className="text-blue-600"
              />
              All Records
            </label>
          </div>
        </div>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ backgroundColor: '#1a2744' }}>
              <th className="text-left p-3 text-white font-medium">WO #</th>
              <th className="text-left p-3 text-white font-medium">Legal Tech</th>
              <th className="text-left p-3 text-white font-medium">Status</th>
              <th className="text-left p-3 text-white font-medium">Type Record</th>
              <th className="text-left p-3 text-white font-medium">Ordered By</th>
              <th className="text-left p-3 text-white font-medium">Stop Location</th>
              <th className="text-left p-3 text-white font-medium">Atty Firm</th>
              <th className="text-left p-3 text-white font-medium">Patient Name</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-4 text-center text-gray-500">Loading...</td></tr>
            ) : results.length === 0 ? (
              <tr><td colSpan={8} className="p-4 text-center text-gray-500">No results found</td></tr>
            ) : (
              results.map((r, i) => (
                <tr
                  key={i}
                  onClick={() => navigate(`/causes/${r.cause_id}`)}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  <td className="p-3">{r.wo_number}</td>
                  <td className="p-3">{r.legal_tech}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-800'}`}>
                      {r.status}
                    </span>
                    {r.rush_order && (
                      <span className="ml-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">RUSH</span>
                    )}
                  </td>
                  <td className="p-3">{r.type_of_record}</td>
                  <td className="p-3">{r.ordered_by}</td>
                  <td className="p-3">{r.stop_location}</td>
                  <td className="p-3">{r.attorney_firm}</td>
                  <td className="p-3">{r.patient_name}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
